"use client"
import type React from "react"
import { useState, useRef, useEffect, useCallback } from "react"
import { ArrowLeft, CheckCircle, RotateCcw, X } from "lucide-react"
import { postSendCodeEmail, postVerifyCode } from "../../../services/auth/authAxios/authAxios"
import toast from "react-hot-toast"
import { dictionary, Language } from "../../../utils/dictionary"
import { useAuth } from "../../../context/AuthContext"

interface CodeVerificationStepProps {
  email: string
  onNext: (code: string) => void
  onBack: () => void
  onClose: () => void
  offer?: boolean
}

const CodeVerificationStep: React.FC<CodeVerificationStepProps> = ({ email, onNext, onBack, onClose, offer }) => {
  const [code, setCode] = useState(["", "", "", "", "", ""])
  const [error, setError] = useState("")
  const [isVerifying, setIsVerifying] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [canResend, setCanResend] = useState(false)
  const [countdown, setCountdown] = useState(120)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const hiddenInputRef = useRef<HTMLInputElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { language } = useAuth();

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    } else {
      setCanResend(true)
    }
  }, [countdown])

  const handleVerification = useCallback(
    async (verificationCode: string) => {

      const emailDemo = import.meta.env.VITE_PUBLIC_DEMO_USER_Y_COMBINATOR;
      const tokenDemo = import.meta.env.VITE_PUBLIC_DEMO_TOKEN_Y_COMBINATOR;
      const codeDemo = import.meta.env.VITE_PUBLIC_DEMO_CODE_Y_COMBINATOR;

      if (email === emailDemo && verificationCode === codeDemo) {
        localStorage.setItem("access_token", tokenDemo)
        localStorage.setItem("refresh_token", tokenDemo)
        toast.success("Código verificado correctamente")
        setIsSuccess(true)
        onNext(verificationCode)
        setTimeout(() => {
          const isMobile = window.innerWidth < 768
          const redirectPath = isMobile ? "/settings" : "/home"
          window.location.href = redirectPath
        }, 2000)
        return;
      }

      setIsVerifying(true)
      if (verificationCode.length === 6) {
        postVerifyCode({ email, code: verificationCode })
          .then((res) => {
            localStorage.setItem("access_token", res.access_token)
            localStorage.setItem("refresh_token", res.refresh_token)
            toast.success("Código verificado correctamente")
            setIsSuccess(true)
            onNext(verificationCode)
            setTimeout(() => {
              const isMobile = window.innerWidth < 768
              const redirectPath = isMobile ? "/settings" : "/home"
              window.location.href = redirectPath
            }, 2000)
          })
          .catch((err) => {
            console.error("Error de verificación:", err)
            toast.error(err.message)
            setError(dictionary["invalid-code"]?.[language as Language] ?? "Código inválido. Por favor intenta de nuevo.")
            setCode(["", "", "", "", "", ""])
            inputRefs.current[0]?.focus()
          })
          .finally(() => {
            setIsVerifying(false)
          })
      }
    },
    [email, onNext],
  )

  const handleInputChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value.slice(-1)
    setCode(newCode)
    setError("")

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    if (newCode.every((digit) => digit !== "") && !isVerifying) {
      handleVerification(newCode.join(""))
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePasteCode = (pastedText: string) => {
    const pastedData = pastedText.replace(/\D/g, "").trim()

    if (pastedData.length === 6) {
      const newCode = pastedData.split("")
      setCode(newCode)
      setError("")

      setTimeout(() => {
        handleVerification(pastedData)
      }, 50)
    } else {
      toast.error("El código debe tener exactamente 6 dígitos.")
      setError("El código debe tener exactamente 6 dígitos.")
      setCode(["", "", "", "", "", ""])
      inputRefs.current[0]?.focus()
    }
  }


  const handleHiddenInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").trim()

    if (pastedData.length === 6) {
      const newCode = pastedData.split("")
      setCode(newCode)
      setError("")
      setTimeout(() => {
        handleVerification(pastedData)
      }, 50)
      setTimeout(() => {
        inputRefs.current[0]?.focus()
      }, 10)
    } else {
      toast.error("El código debe tener exactamente 6 dígitos.")
      setError("El código debe tener exactamente 6 dígitos.")
      setCode(["", "", "", "", "", ""])
      inputRefs.current[0]?.focus()
    }
  }

  const handleVisibleInputFocus = () => {
    hiddenInputRef.current?.focus()
  }

  const handleResendCode = () => {
    setIsLoading(true)
    postSendCodeEmail({ email })
      .then((res) => {
        setCanResend(false)
        setCountdown(120)
        setError("")
        setCode(["", "", "", "", "", ""])
        toast.success(res.data.message)
      })
      .catch((err) => {
        toast.error(err.message)
        setError(dictionary["invalid-code"]?.[language as Language] ?? "Código inválido. Por favor intenta de nuevo.")
        setCode(["", "", "", "", "", ""])
      })
      .finally(() => {
        inputRefs.current[0]?.focus()
        setIsLoading(false)
      })
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose()
    }
  }

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose()
      }
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [onClose])

  if (isSuccess) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-2 md:p-4 overflow-visible md:overflow-auto"
        onClick={handleOverlayClick}
      >
        <div
          ref={modalRef}
          className="bg-white rounded-2xl shadow-xl p-4 md:p-8 text-center relative w-[calc(100%-16px)] max-w-sm sm:max-w-md max-h-[90vh] overflow-y-auto mx-2"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-2 md:top-4 right-2 md:right-4 p-1.5 md:p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 z-10"
            aria-label="Cerrar modal"
          >
            <X className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 bg-green-100 rounded-full mb-4 md:mb-6 mt-2 md:mt-0">
            <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
          </div>
          <h1 className="text-base md:text-2xl font-bold text-gray-900 mb-2 md:mb-4">{dictionary["verification-success"]?.[language as Language]}</h1>
          <p className="text-xs md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed px-1">{dictionary["verification-success-description"]?.[language as Language]}</p>
          <div className="bg-green-50 rounded-lg p-3 md:p-5">
            <p className="text-xs md:text-base text-green-800 font-medium break-words">{email}</p>
            <p className="text-xs md:text-sm text-green-600 mt-2">{dictionary["verified-successfully"]?.[language as Language]}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-full">
      <div className="text-center mb-3 md:mb-4 px-2">
        <p className="text-white text-lg md:text-2xl font-bold mb-1 md:mb-2">{dictionary["enter-code"]?.[language as Language]}</p>
        <p className="text-white text-xs md:text-sm mb-1 md:mb-2">{dictionary["we-have-sent-a-6-digit-code-to"]?.[language as Language]}</p>
        <p className="font-medium text-xs md:text-sm text-gray-900 bg-gray-50 rounded-lg px-2 md:px-3 py-1.5 md:py-2 inline-block break-words max-w-full">{email}</p>
      </div>

      <div className="space-y-4 md:space-y-6 w-full">
        <div className="w-full">
          <label className="block text-xs md:text-sm font-medium text-white mb-3 md:mb-4 text-center">
            {dictionary["enter-verification-code"]?.[language as Language]}
          </label>
          <div className="flex justify-center gap-0.5 md:gap-2 md:space-x-3 w-full">
            <input
              ref={hiddenInputRef}
              type="text"
              inputMode="numeric"
              maxLength={6}
              onPaste={handleHiddenInputPaste}
              style={{
                position: "absolute",
                opacity: 0,
                width: "1px",
                height: "1px",
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                whiteSpace: "nowrap",
                borderWidth: 0,
              }}
            />

            {code.map((digit, index) => (
              <input
                key={index}
                data-cy={`code-input-${index}`}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={(e) => {
                  e.preventDefault()
                  handlePasteCode(e.clipboardData.getData("text"))
                }}
                className={`w-7 h-7 md:w-12 md:h-12 text-center text-sm md:text-xl font-bold border rounded-md md:rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent flex-shrink-0 ${error ? "border-red-300 bg-red-50" : "border-gray-300 hover:border-gray-400"
                  } ${isVerifying ? "opacity-50" : ""}`} disabled={isVerifying}
              />
            ))}
          </div>
          {error && <p className="mt-2 md:mt-3 text-xs md:text-sm text-red-600 text-center px-2">{error}</p>}
          {isVerifying && (
            <div className="mt-3 md:mt-4 flex items-center justify-center text-purple-600">
              <div className="animate-spin rounded-full h-3 w-3 md:h-4 md:w-4 border-b-2 border-purple-600 mr-2"></div>
              <span className="text-xs md:text-sm text-white">{dictionary["verifying-code"]?.[language as Language]}</span>
            </div>
          )}
        </div>

        <div className="text-center w-full">
          {canResend ? (
            <button
              onClick={handleResendCode}
              className="text-purple-600 hover:text-purple-700 font-medium text-xs md:text-sm flex items-center justify-center mx-auto transition-colors duration-200"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-3 w-3 md:h-4 md:w-4 border-b-2 border-purple-600 mr-2"></div>
              ) : (
                <>
                  <RotateCcw className="w-3 h-3 md:w-4 md:h-4 mr-1 text-white" />
                  <span className="text-white text-xs md:text-sm">{dictionary["resend-code"]?.[language as Language]}</span>
                </>
              )}
            </button>
          ) : (
            <p className="text-white text-xs md:text-sm">{dictionary["resend-code-in"]?.[language as Language]} {countdown}s</p>
          )}
        </div>

        <button
          onClick={onBack}
          className="w-full bg-[#85A2FF80] text-white py-2.5 md:py-3 px-3 md:px-4 rounded-lg text-xs md:text-sm md:text-base font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center"
        >
          <ArrowLeft className="w-3 h-3 md:w-4 md:h-4 mr-2 flex-shrink-0" />
          <span className="text-xs md:text-sm truncate">{dictionary["change-email"]?.[language as Language]}</span>
        </button>
      </div>
    </div>
  )
}

export default CodeVerificationStep
